// Nature's Rules: two labs where one simple rule makes something beautiful. Snowflake: every
// stroke is copied six times around the center (six-fold symmetry). Flock: three rules, no
// leader. Ported from the widgets; lesson, then do it; Explore after.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { makeFlock, stepFlock, setCount, meanNearest, alignment, type Flock, type Rules, type Threat } from './boids';

type Tab = 'snow' | 'flock';
type Kind = 'strokes' | 'twocolors' | 'twoflakes' | 'flockup' | 'scare' | 'predict' | 'scatter' | 'choice';

interface Step {
  tab: Tab; lesson: string; task: string; kind: Kind;
  choices?: string[]; answer?: number;
  /** for 'predict': the answer she must predict, then the sim confirms */
  rules?: Rules;              // rules to start the flock step with
  why: string;
}

function tierFor(level: number): 1 | 2 | 3 { return level <= 3 ? 1 : level <= 6 ? 2 : 3; }

const STEPS: Record<1 | 2 | 3, Step[]> = {
  1: [
    { tab: 'snow', lesson: 'A snowflake grows the same way on all six sides at once. So you only draw one arm, and the rule copies it six times.', task: 'Draw at least 3 strokes on the snowflake, with 6 arms, then tap Check.', kind: 'strokes', why: 'Every stroke you drew appeared on all six arms. One rule, six copies.' },
    { tab: 'flock', lesson: 'Birds in a flock follow three tiny rules: stay close, fly the same way, give space. Nobody is the leader.', task: 'The rules are off. Turn up Stay close and Fly the same way until the birds flock together, then tap Check.', kind: 'flockup', rules: { kC: 0, kA: 0, kS: 5 }, why: 'With all three rules on, a flock appears by itself. No bird is in charge.' },
    { tab: 'flock', lesson: 'When something scares a flock it bursts apart, then the same three rules pull it back together.', task: 'Tap the sky to scare the flock, then wait for them to regroup, then tap Check.', kind: 'scare', rules: { kC: 5, kA: 5, kS: 5 }, why: 'The scare pushed every bird away. The rules never stopped, so the flock came back.' },
  ],
  2: [
    { tab: 'snow', lesson: 'Water molecules lock together in six-sided rings when they freeze. That hexagon shape is why snowflakes have six arms.', task: 'Why do snowflakes have six arms?', kind: 'choice', choices: ['Water molecules lock into hexagons', 'Because it is cold', 'The wind shapes them', 'It is just luck'], answer: 0, why: 'The six-sided ring of frozen water is the rule. The arms copy that shape as the flake grows.' },
    { tab: 'flock', lesson: 'Take one rule away and the flock changes. What do you think happens with no Stay close?', task: 'Predict: with Stay close turned off, the birds will...', kind: 'predict', choices: ['Drift apart into little streams', 'Squish into one tight ball', 'Stop moving', 'Fly in a perfect circle'], answer: 0, rules: { kC: 5, kA: 5, kS: 5 }, why: 'Without Stay close, nothing pulls them together. They still fly the same way, so they drift apart in streams.' },
    { tab: 'snow', lesson: 'Real snowflakes have branches on branches. Add detail to one arm and the rule copies it everywhere.', task: 'Draw a snowflake using 2 different colors and at least 4 strokes, then tap Check.', kind: 'twocolors', why: 'More strokes on one arm means more detail on all six. That is how a flake gets lacy.' },
  ],
  3: [
    { tab: 'snow', lesson: 'No two snowflakes are the same, because each one falls through slightly different air. A tiny change makes a different flake.', task: 'Draw a flake and tap Keep it. Then change ONE thing (a stroke, a color, the arms) and tap Keep it again.', kind: 'twoflakes', why: 'One small change, a whole new flake. Same rule, different path through the clouds.' },
    { tab: 'flock', lesson: 'Nobody leads the flock. If the rules that connect the birds are all off, there is no flock at all.', task: 'Find the setting where the birds scatter and never flock. Then tap Check.', kind: 'scatter', rules: { kC: 5, kA: 5, kS: 5 }, why: 'With Stay close and Fly the same way both off, each bird is on its own. The flock was only ever the rules.' },
    { tab: 'flock', lesson: 'A flock turns together with no signal and no boss. Each bird only watches its neighbors.', task: 'Who decides where the flock goes?', kind: 'choice', choices: ['No one: each bird follows its neighbors', 'The biggest bird', 'The bird in front', 'The hawk'], answer: 0, why: 'Every bird copies its neighbors a little. Add that up and the whole flock turns as one.' },
  ],
};

interface Stroke { c: string; w: number; p: [number, number][] }
const COLS = ['#ffffff', '#85B7EB', '#5DCAA5', '#AFA9EC', '#ED93B1'];

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.currentLevel();
  const tier = tierFor(level);
  const steps = STEPS[tier];
  let si = 0;
  let explore = false;
  let attempts = 0, correct = 0, missed = false;
  const t0 = performance.now();
  let tStep = performance.now();
  let tab: Tab = steps[0].tab;

  // ---- snowflake state ----
  let strokes: Stroke[] = [];
  let cur: Stroke | null = null;
  let col = COLS[0], bw = 4, arms = 6, guides = true;
  const kept: string[] = [];
  // ---- flock state ----
  let rules: Rules = { kC: 5, kA: 5, kS: 5 };
  let flock: Flock = makeFlock(90, 380, 320, ctx.rng);
  let hawk: { x: number; y: number; vx: number; vy: number } | null = null;
  let scare: [number, number] | null = null, scareT = 0;
  let scared = false, regrouped = false;
  let predicted: number | null = null;

  const lesson = h('div.card', { style: { borderLeft: '8px solid var(--science)' } }, h('p', null, ''));
  const task = h('p.center', { style: { fontWeight: '800', minHeight: '44px' } });
  const tabs = h('div.tabs', { style: { padding: '0 0 8px' } });
  const stage = h('div');
  const readout = h('div.row', { style: { justifyContent: 'space-between', fontWeight: '800', minHeight: '24px' } }, h('span', null, ''), h('span.muted.small', null, ''));
  const controls = h('div.stack');
  const choices = h('div.grid2');
  host.append(lesson, task, tabs, stage, readout, controls, choices);
  const S = makeSurface(stage, { aspect: 0.84 });

  function renderTabs() {
    clear(tabs);
    for (const [id, label] of [['snow', 'Snowflake'], ['flock', 'Flock']] as [Tab, string][]) {
      const locked = !explore && steps[si] && steps[si].tab !== id;
      tabs.append(h('button.tab' + (tab === id ? '.on' : ''), { disabled: locked, style: { opacity: locked ? '.45' : '1' }, onclick: () => { tab = id; renderTabs(); renderControls(); } }, label));
    }
  }

  function slider(label: string, min: number, max: number, value: number, step: number, on: (v: number) => void): HTMLElement {
    const out = h('span', { style: { minWidth: '28px', textAlign: 'right', fontWeight: '800' } }, String(value));
    const inp = h('input', { type: 'range', min: String(min), max: String(max), step: String(step), value: String(value), 'aria-label': label }) as HTMLInputElement;
    inp.addEventListener('input', () => { const v = Number(inp.value); out.textContent = String(v); on(v); });
    return h('div.row', null, h('span.muted.small', { style: { minWidth: '112px' } }, label), inp, out);
  }

  function renderControls() {
    clear(controls); clear(choices);
    const st = explore ? null : steps[si];
    if (tab === 'snow') {
      const sw = h('div.swatches', null, COLS.map(c => h('button.swatch' + (c === col ? '.on' : ''), { style: { background: c, border: c === col ? '3px solid var(--text)' : '3px solid var(--line)' }, 'aria-label': 'color', onclick: () => { col = c; renderControls(); } })));
      controls.append(h('div.row', null, h('span.muted.small', { style: { minWidth: '60px' } }, 'Color'), sw));
      controls.append(slider('Brush', 1, 12, bw, 1, v => { bw = v; }));
      controls.append(slider('Arms', 2, 12, arms, 1, v => { arms = v; ctx.say(arms === 6 ? 'Six arms, just like a real snowflake.' : 'Real snowflakes always have 6 arms!'); }));
      const row = h('div.grid2', null,
        h('button.btn.small', { onclick: () => { strokes.pop(); } }, 'Undo'),
        h('button.btn.small.ghost', { onclick: () => { strokes = []; ctx.say('Fresh snow. Draw something!'); } }, 'Clear'),
        h('button.btn.small', { onclick: () => { guides = !guides; } }, 'Guides on / off'),
        explore ? h('button.btn.small', { onclick: surpriseFlake }, 'Surprise me') : h('span'),
      );
      controls.append(row);
      if (st && (st.kind === 'strokes' || st.kind === 'twocolors')) controls.append(h('button.btn.primary.block', { onclick: checkSnow }, 'Check'));
      if (st && st.kind === 'twoflakes') controls.append(h('button.btn.primary.block', { onclick: keepFlake }, kept.length ? 'Keep it (second flake)' : 'Keep it (first flake)'));
      if (st && st.kind === 'choice') addChoices(st);
    } else {
      controls.append(slider('Stay close', 0, 10, rules.kC, 1, v => { rules.kC = v; describe(); }));
      controls.append(slider('Fly the same way', 0, 10, rules.kA, 1, v => { rules.kA = v; describe(); }));
      controls.append(slider('Give space', 0, 10, rules.kS, 1, v => { rules.kS = v; describe(); }));
      controls.append(slider('Birds', 10, 160, flock.B.length, 10, v => setCount(flock, v, ctx.rng)));
      controls.append(h('div.grid2', null,
        h('button.btn.small', { onclick: () => { if (hawk) { hawk = null; ctx.say('The hawk flew away.'); } else { hawk = { x: 0, y: flock.h / 2, vx: 2, vy: 0 }; ctx.say('Here comes the hawk! Watch the flock split and swirl.'); } } }, 'Hawk on / off'),
        h('button.btn.small.ghost', { onclick: () => { rules = { kC: 5, kA: 5, kS: 5 }; renderControls(); ctx.say('Back to normal flocking.'); } }, 'Reset rules'),
      ));
      if (st && (st.kind === 'flockup' || st.kind === 'scare' || st.kind === 'scatter' || (st.kind === 'predict' && predicted !== null))) controls.append(h('button.btn.primary.block', { onclick: checkFlock }, 'Check'));
      if (st && (st.kind === 'choice' || (st.kind === 'predict' && predicted === null))) addChoices(st);
    }
  }

  function addChoices(st: Step) { st.choices!.forEach((c, i) => choices.append(h('button.choice', { style: { fontSize: '16px' }, onclick: () => answer(i) }, c))); }

  function describe() {
    const { kC, kA, kS } = rules;
    let d = '';
    if (kC === 0 && kA === 0) d = 'No rules to follow, so every bird flies off on its own.';
    else if (kS === 0 && kC >= 4) d = 'No personal space. They squish into a tight clump!';
    else if (kA === 0) d = 'They stay near each other but point every which way, like a swarm of bugs.';
    else if (kC === 0) d = 'They fly the same way but drift apart into little streams.';
    else if (kS >= 9) d = 'Lots of personal space. The flock spreads out wide.';
    if (d) ctx.say(d);
  }

  function showStep() {
    const st = steps[si];
    (lesson.firstElementChild as HTMLElement).textContent = st.lesson;
    task.textContent = `Step ${si + 1} of ${steps.length}: ${st.task}`;
    tab = st.tab; missed = false; tStep = performance.now(); predicted = null; scared = false; regrouped = false;
    if (st.rules) { rules = { ...st.rules }; flock = makeFlock(90, S.w || 380, S.h || 320, ctx.rng); hawk = null; }
    if (st.kind === 'twoflakes') { kept.length = 0; strokes = []; }
    if (st.kind === 'strokes' || st.kind === 'twocolors') strokes = [];
    renderTabs(); renderControls();
    ctx.speak(st.task);
  }

  function pass(st: Step) {
    if (!missed) { attempts++; correct++; }
    ctx.report({ skill: st.tab === 'snow' ? 'physics' : 'biology', level, correct: !missed, hintUsed: missed, ms: performance.now() - tStep, problemId: `nature:${tier}:${si}` });
    ctx.sound('ding'); ctx.say(st.why);
    if (st.kind === 'strokes' || st.kind === 'twocolors') ctx.charm('charm.sixarms');
    if (st.kind === 'scatter') ctx.charm('charm.noleader');
    si++;
    if (si >= steps.length) {
      explore = true;
      task.textContent = 'Round complete. Both labs are open: explore!';
      renderTabs(); renderControls();
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 1600);
      return;
    }
    setTimeout(showStep, 1800);
  }
  function fail(hint: string) {
    if (!missed) { attempts++; missed = true; ctx.report({ skill: steps[si].tab === 'snow' ? 'physics' : 'biology', level, correct: false, hintUsed: false, ms: performance.now() - tStep, problemId: `nature:${tier}:${si}` }); }
    ctx.sound('buzz'); ctx.say(hint);
  }

  function answer(i: number) {
    const st = steps[si];
    if (st.kind === 'predict') {
      predicted = i;
      if (i !== st.answer) { fail('Hmm. Try it and see: turn Stay close down to 0 and watch, then tap Check.'); }
      else ctx.say('Now test it: turn Stay close down to 0, watch for a few seconds, then tap Check.');
      renderControls();
      return;
    }
    if (i === st.answer) pass(st);
    else fail(st.tab === 'snow' ? 'Think about what water does when it freezes: it locks into a shape.' : 'Watch the flock turn. Does any one bird go first?');
  }

  // ---- snowflake checks ----
  function flakeKey(): string { return JSON.stringify({ arms, s: strokes.map(s => [s.c, s.w, s.p.map(p => [Math.round(p[0] * 50), Math.round(p[1] * 50)])]) }); }
  function checkSnow() {
    const st = steps[si];
    if (st.kind === 'strokes') {
      if (arms !== 6) { fail('Real snowflakes have 6 arms. Set Arms to 6.'); return; }
      if (strokes.length < 3) { fail(`You have ${strokes.length} stroke${strokes.length === 1 ? '' : 's'}. Draw at least 3 on one arm.`); return; }
      pass(st);
    } else if (st.kind === 'twocolors') {
      const colors = new Set(strokes.map(s => s.c));
      if (strokes.length < 4) { fail(`${strokes.length} strokes so far. Draw at least 4.`); return; }
      if (colors.size < 2) { fail('Pick a second color and add a stroke in it.'); return; }
      pass(st);
    }
  }
  function keepFlake() {
    const st = steps[si];
    if (!strokes.length) { fail('Draw something first, then keep it.'); return; }
    kept.push(flakeKey());
    if (kept.length === 1) { ctx.say('Kept. Now change one thing: add a stroke, change a color, or move the Arms slider. Then keep it again.'); ctx.sound('pop'); renderControls(); return; }
    if (kept[0] === kept[1]) { kept.pop(); fail('That is the same flake. Change one thing, then keep it again.'); return; }
    pass(st);
  }
  function surpriseFlake() {
    strokes = [];
    const L = 0.82 + ctx.rng() * 0.1;
    const c1 = COLS[Math.floor(ctx.rng() * COLS.length)], c2 = COLS[Math.floor(ctx.rng() * COLS.length)];
    const seg = (x1: number, y1: number, x2: number, y2: number, c: string, w: number) => strokes.push({ c, w, p: [[x1, y1], [x2, y2]] });
    seg(0, 0, 0, -L, c1, 4);
    const nb = 2 + Math.floor(ctx.rng() * 3);
    for (let i = 0; i < nb; i++) {
      const tt = 0.25 + 0.6 * (i + ctx.rng() * 0.6) / nb, len = (0.12 + ctx.rng() * 0.25) * (1.1 - tt), y = -L * tt, ang = Math.PI / 3;
      seg(0, y, Math.sin(ang) * len, y - Math.cos(ang) * len, i % 2 ? c2 : c1, 3);
      if (ctx.rng() < 0.5) { const t2 = 0.6, bx = Math.sin(ang) * len * t2, by = y - Math.cos(ang) * len * t2; seg(bx, by, bx, by - len * 0.4, c2, 2); }
    }
    if (ctx.rng() < 0.7) { const r = 0.1 + ctx.rng() * 0.15; seg(0, -r, Math.sin(Math.PI / arms) * r, -Math.cos(Math.PI / arms) * r, c2, 3); }
    if (ctx.rng() < 0.6) strokes.push({ c: c2, w: 6, p: [[0, -L]] });
    ctx.say('Every real snowflake is different. So is every one of these!');
  }

  // ---- flock checks ----
  function checkFlock() {
    const st = steps[si];
    const nn = meanNearest(flock), al = alignment(flock);
    if (st.kind === 'flockup') {
      if (rules.kC >= 3 && rules.kA >= 3 && nn < 26 && al > 0.45) pass(st);
      else if (rules.kC < 3 || rules.kA < 3) fail('Turn both Stay close and Fly the same way up to at least 3, then give them a moment.');
      else fail('Give the birds a few seconds to find each other, then check again.');
    } else if (st.kind === 'scare') {
      if (!scared) fail('Tap the sky first to scare them.');
      else if (!regrouped) fail('Wait a moment: they are still regrouping.');
      else pass(st);
    } else if (st.kind === 'predict') {
      if (rules.kC > 0) fail('Turn Stay close all the way down to 0 to test your prediction.');
      else if (nn < 24) fail('Give it a few seconds: without Stay close, watch them drift apart.');
      else pass(st);
    } else if (st.kind === 'scatter') {
      if (rules.kC === 0 && rules.kA === 0 && nn > 20 && al < 0.5) pass(st);
      else if (rules.kC > 0 || rules.kA > 0) fail('A flock needs the rules that connect birds. Which two sliders connect them? Turn them off.');
      else fail('Give them a few seconds to scatter, then check again.');
    }
  }

  // ---- pointer ----
  function ptSnow(p: { x: number; y: number }): [number, number] { const sz = Math.min(S.w, S.h); const ox = (S.w - sz) / 2, oy = (S.h - sz) / 2; return [((p.x - ox) / sz) * 2 - 1, ((p.y - oy) / sz) * 2 - 1]; }
  const unPointer = attachPointer(S.canvas, {
    down(p) {
      if (tab === 'snow') { cur = { c: col, w: bw, p: [ptSnow(p)] }; }
      else { scare = [p.x, p.y]; scareT = 60; scared = true; regrouped = false; }
    },
    move(p) {
      if (tab === 'snow') { if (!cur) return; const q = ptSnow(p), l = cur.p[cur.p.length - 1]; if (Math.hypot(q[0] - l[0], q[1] - l[1]) > 0.006) cur.p.push(q); }
      else if (scareT > 0) { scare = [p.x, p.y]; scareT = 60; }
    },
    up() { if (tab === 'snow' && cur) { strokes.push(cur); cur = null; } },
  });

  function update() {
    if (tab !== 'flock' && !explore) return;
    if (flock.w !== S.w || flock.h !== S.h) { if (S.w > 0) { flock.B.forEach(b => { b.x = b.x / flock.w * S.w; b.y = b.y / flock.h * S.h; }); flock.w = S.w; flock.h = S.h; } }
    const threats: Threat[] = [];
    if (scareT > 0 && scare) threats.push({ x: scare[0], y: scare[1], r: 90 });
    if (hawk) threats.push({ x: hawk.x, y: hawk.y, r: 70 });
    stepFlock(flock, rules, ctx.rng, threats);
    if (hawk) {
      let best: [number, number] | null = null, bd = 1e9;
      for (const b of flock.B) { const dx = b.x - hawk.x, dy = b.y - hawk.y, d = dx * dx + dy * dy; if (d < bd) { bd = d; best = [dx, dy]; } }
      if (best) { const d = Math.sqrt(bd) || 1; hawk.vx += best[0] / d * 0.08; hawk.vy += best[1] / d * 0.08; }
      const s = Math.hypot(hawk.vx, hawk.vy), hm = 2.9; if (s > hm) { hawk.vx *= hm / s; hawk.vy *= hm / s; }
      hawk.x = (hawk.x + hawk.vx + flock.w) % flock.w; hawk.y = (hawk.y + hawk.vy + flock.h) % flock.h;
    }
    if (scareT > 0) scareT--;
    if (scared && scareT === 0 && !regrouped && meanNearest(flock) < 26 && alignment(flock) > 0.45) regrouped = true;
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    if (tab === 'snow') drawSnow(c, W, H); else drawFlock(c, W, H);
  }

  function pathStroke(c: CanvasRenderingContext2D, st: Stroke, sz: number) {
    if (st.p.length === 1) { c.beginPath(); c.arc(st.p[0][0] * sz / 2, st.p[0][1] * sz / 2, st.w * sz / 760, 0, Math.PI * 2); c.fillStyle = st.c; c.fill(); return; }
    c.beginPath(); c.moveTo(st.p[0][0] * sz / 2, st.p[0][1] * sz / 2);
    for (let i = 1; i < st.p.length; i++) c.lineTo(st.p[i][0] * sz / 2, st.p[i][1] * sz / 2);
    c.strokeStyle = st.c; c.lineWidth = st.w * sz / 380; c.stroke();
  }
  function drawSnow(c: CanvasRenderingContext2D, W: number, H: number) {
    const sz = Math.min(W, H);
    c.fillStyle = '#0b1f3a'; c.fillRect(0, 0, W, H);
    c.save(); c.translate(W / 2, H / 2);
    if (guides) {
      c.strokeStyle = 'rgba(255,255,255,0.12)'; c.lineWidth = 1; c.setLineDash([3, 5]);
      for (let k = 0; k < arms * 2; k++) { const a = k * Math.PI / arms; c.beginPath(); c.moveTo(0, 0); c.lineTo(Math.cos(a) * sz * 0.48, Math.sin(a) * sz * 0.48); c.stroke(); }
      c.beginPath(); c.arc(0, 0, sz * 0.48, 0, Math.PI * 2); c.stroke(); c.setLineDash([]);
    }
    c.lineCap = 'round'; c.lineJoin = 'round';
    const all = cur ? [...strokes, cur] : strokes;
    for (const st of all) for (let k = 0; k < arms; k++) { c.save(); c.rotate(k * 2 * Math.PI / arms); pathStroke(c, st, sz); c.scale(1, -1); pathStroke(c, st, sz); c.restore(); }
    c.restore();
    (readout.children[0] as HTMLElement).textContent = `${strokes.length} stroke${strokes.length === 1 ? '' : 's'}`;
    (readout.children[1] as HTMLElement).textContent = `${arms} arms`;
  }
  function tri(c: CanvasRenderingContext2D, x: number, y: number, vx: number, vy: number, size: number, fill: string) {
    const a = Math.atan2(vy, vx), co = Math.cos(a), s = Math.sin(a);
    c.fillStyle = fill; c.beginPath(); c.moveTo(x + co * size, y + s * size); c.lineTo(x - co * size * 0.7 - s * size * 0.6, y - s * size * 0.7 + co * size * 0.6); c.lineTo(x - co * size * 0.35, y - s * size * 0.35); c.lineTo(x - co * size * 0.7 + s * size * 0.6, y - s * size * 0.7 - co * size * 0.6); c.closePath(); c.fill();
  }
  function drawFlock(c: CanvasRenderingContext2D, W: number, H: number) {
    c.fillStyle = '#B5D4F4'; c.fillRect(0, 0, W, H);
    c.fillStyle = 'rgba(255,255,255,0.55)';
    for (const [x, y, r] of [[0.15, 0.2, 26], [0.22, 0.22, 20], [0.7, 0.35, 30], [0.78, 0.33, 22], [0.45, 0.8, 24], [0.52, 0.78, 18]]) { c.beginPath(); c.arc(x * W, y * H, r, 0, Math.PI * 2); c.fill(); }
    if (scareT > 0 && scare) { c.strokeStyle = `rgba(4,44,83,${scareT / 60 * 0.5})`; c.lineWidth = 2; c.beginPath(); c.arc(scare[0], scare[1], 90 * (1 - scareT / 60) + 10, 0, Math.PI * 2); c.stroke(); }
    for (const b of flock.B) tri(c, b.x, b.y, b.vx, b.vy, 6, '#042C53');
    if (hawk) tri(c, hawk.x, hawk.y, hawk.vx, hawk.vy, 13, '#993C1D');
    (readout.children[0] as HTMLElement).textContent = `${flock.B.length} birds`;
    (readout.children[1] as HTMLElement).textContent = `Spacing ${Math.round(meanNearest(flock))}`;
  }

  const loop = makeLoop(update, draw);
  loop.start();
  showStep();
  return { destroy() { loop.stop(); unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'science.nature-rules',
  title: "Nature's Rules",
  blurb: 'One rule makes a snowflake. Three rules make a flock. Draw one, fly the other.',
  category: 'science',
  skills: ['biology', 'physics'],
  levelRange: [1, 10],
  unlock: { stars: 11 },
  mount,
};
export default mod;
